






















































































































































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
//  @types
import { Header, Row } from "@/types/VDataTable";
import { UserProfile } from "@/types/userProfile";
//  @components
import { headers } from "@/components/Settings/UserList/headers";
import SortableArrows from "@/components/SortableArrows.vue";
import DataCell from "./DataCell.vue";
import Tooltip from "@/components/Tooltip.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import APIKeyModal from "./APIKeyModal.vue";
import UserDataModal from "./UserDataModal.vue";

//  @modules
import { getModule } from "vuex-module-decorators";
import SnackbarModule from "@/store/clients/Snackbar.module";
import UsersModule from "@/store/clients/Users.module";
import UserModule from "@/store/clients/User.module";
import UserService from "@/services/UserService";

const Users = getModule(UsersModule, store);
const User = getModule(UserModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {
    SortableArrows,
    DataCell,
    Tooltip,
    ConfirmDialog,
    APIKeyModal,
    UserDataModal,
  },
})
export default class DataTable extends Vue {
  @Ref("confirm") confirm!: any;
  // @Data
  loading = false;
  isLoadingTextDisplay = false;
  sortDesc = false;
  searchQuery = "";
  headers: Header[] = headers;
  sortBy = "";
  pageLimit = -1; // -1 show all items by default
  pageCount = 0;
  currentItems: Row[] = [];
  currentPage = 1;
  APIKeyModal = false;
  selectedUser: UserProfile | null = null;
  UserDataModal = false;

  @Watch("UserDataModal")
  onUserDataModalClose(newVal: boolean) {
    if (!newVal) this.selectedUser = null;
  }

  // @Getters
  get isLessThanLimit(): boolean {
    return this.rows.length <= this.pageLimit;
  }

  get skeletonStructure(): object {
    return { "table-row": `table-cell@6` };
  }

  get filteredHeaders(): Header[] {
    const result = this.headers.filter(header => {
      return true;
    });
    return result;
  }

  get rows(): any {
    let rows = this.users.map((u, index) => {
      return {
        index: index,
        userId: u.id,
        userName: {
          value: u.name,
        },
        role: {
          value: u.role,
        },
        email: {
          value: u.email,
        },
        apiKey: {
          value: u.apiKey,
        },
        lastActivityDate: {
          value: u.lastActivityDate ? moment.utc(u.lastActivityDate).format("DD. MMM, YYYY") : "",
          subtitle: u.lastActivityDate ? moment.utc(u.lastActivityDate).fromNow() : "",
        },
        actions: {
          value: ["edit", "apikey", "delete"],
        },
      };
    });

    // Sort by apiKey: users with API keys first, then users without
    rows = rows.sort((a, b) => {
      const hasApiKeyA = a.apiKey.value && a.apiKey.value !== "";
      const hasApiKeyB = b.apiKey.value && b.apiKey.value !== "";

      // If A has API key and B doesn't, A comes first
      if (hasApiKeyA && !hasApiKeyB) return -1;
      // If B has API key and A doesn't, B comes first
      if (!hasApiKeyA && hasApiKeyB) return 1;
      // Otherwise maintain original order
      return 0;
    });

    // Filter by search query
    if (this.searchQuery && this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      rows = rows.filter(item => {
        // Search by user name or email number
        const nameMatch = item.userName.value ? item.userName.value.toLowerCase().includes(query) : false;
        const emailMatch = item.email ? String(item.email).includes(query) : false;
        return nameMatch || emailMatch;
      });
    }

    return rows;
  }

  get sortedRows() {
    if (!this.sortBy) return this.rows;

    return this.rows.slice().sort((a: any, b: any) => {
      // Special handling for Last Activity Date column
      if (this.sortBy === "lastActivityDate") {
        const dateA = new Date(a[this.sortBy]?.value ?? "");
        const dateB = new Date(b[this.sortBy]?.value ?? "");
        if (this.sortDesc) {
          return dateB.getTime() - dateA.getTime();
        } else {
          return dateA.getTime() - dateB.getTime();
        }
      }

      // Default string/other sorting
      const valueA = a[this.sortBy]?.value ?? "";
      const valueB = b[this.sortBy]?.value ?? "";

      // Normalize values for comparison
      const normalizedA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
      const normalizedB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;

      if (this.sortDesc) {
        return normalizedA < normalizedB ? 1 : normalizedA > normalizedB ? -1 : 0;
      } else {
        return normalizedA > normalizedB ? 1 : normalizedA < normalizedB ? -1 : 0;
      }
    });
  }

  get users(): UserProfile[] {
    return Users.users;
  }

  // @Methods
  cellData(row: Row, header: Header, index: number): any {
    return {
      column: header.value,
      columnName: header.text,
      userId: row.userId,
      type: header.type,
      align: header.align,
      value: row[header.value].value,
      subtitle: row[header.value].subtitle,
      link: row[header.value].link,
    };
  }

  async initializeTable(): Promise<void> {
    try {
      this.loading = true;
      this.isLoadingTextDisplay = false;
      await Users.getAll();

      // Show loading text if data takes more than 2 seconds to load
      setTimeout(() => {
        if (this.loading) {
          this.isLoadingTextDisplay = true;
        }
      }, 2000);
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load users data" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  async onSubmitUserData(payload: { userData: UserProfile; isNewUser: boolean }): Promise<void> {
    if (payload.isNewUser) this.searchQuery = payload.userData.name ?? "";
    try {
      this.loading = true;
      this.isLoadingTextDisplay = true;
      await Users.refreshUsers();
      await this.initializeTable();
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load users data after adding/editing user" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  sort(value: string): void {
    if (this.sortBy === value) {
      this.sortDesc = !this.sortDesc; // Toggle sort direction
      if (!this.sortDesc) this.sortBy = ""; // Reset sorting if toggled off
    } else {
      this.sortBy = value;
      this.sortDesc = false; // Default to ascending order
    }
  }

  updateSortBy(value: string): void {
    if (!value) value = "";
  }

  updateSortDesc(value: boolean): void {
    if (!value) value = false;
  }

  updateItemsPerPage(value: number): void {
    if (!value) value = 20;
    this.pageLimit = value;
  }

  headerClasses(header: any): string[] {
    return [
      header.class,
      {
        sortable: header.sortable,
        "no-sort": this.sortBy !== header.value,
      },
    ];
  }

  headerStyles(header: any): any {
    return {
      width: header.width ? header.width : "auto",
      "min-width": header.width ? header.width : "auto",
    };
  }

  addUser() {
    this.UserDataModal = true;
    this.$emit("add-user");
  }

  onEditUserEvent(userId: string) {
    this.selectedUser = this.users.find(u => u.id === userId) || null;
    this.UserDataModal = true;
    this.$emit("edit-user", userId);
  }

  async onDeleteEvent(userId: string): Promise<void> {
    const userName = this.users.find(u => u.id === userId)?.name || "";
    const confirmed = await this.confirm.open("Delete user?", `Are you sure you want to permanently delete the user '${userName}'?`);
    if (!confirmed) return;
    try {
      await UserService.remove(userId);
      await Users.refreshUsers();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `User ${userName} has been deleted`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to delete user" });
    }
    this.$emit("delete-user", userId);
  }

  onApiKeyEvent(userId: string) {
    this.selectedUser = this.users.find(u => u.id === userId) || null;
    this.APIKeyModal = true;
    this.$emit("api-key-user", this.selectedUser);
  }

  async onGenerateApiKey() {
    Users.refreshUsers();
    this.$emit("onGenerateAPIKey");
  }

  async onRemoveApiKey() {
    Users.refreshUsers();
    this.$emit("onRemoveAPIKey");
  }

  // @Hooks
  async created(): Promise<void> {
    await this.initializeTable();
  }
}
